/**
 * Store Adapter Interfaces
 *
 * Provides dependency injection interfaces for Zustand stores.
 * Enables services to be testable without direct store coupling.
 *
 * Usage:
 * ```typescript
 * class MyService {
 *   constructor(private authStore: AuthStoreAdapter) {}
 *   async doSomething() {
 *     const user = this.authStore.getUser();
 *     this.authStore.setSession(session);
 *   }
 * }
 * ```
 *
 * @see src/services/auth/auth.service.ts for usage examples
 */

import type { User, Session } from '@supabase/supabase-js';

/**
 * Auth Store Adapter Interface
 * Abstracts authentication state management
 */
export interface AuthStoreAdapter {
  // Getters
  getUser: () => User | null;
  getSession: () => Session | null;
  getLoading: () => boolean;

  // Setters
  setUser: (user: User | null) => void;
  setSession: (session: Session | null) => void;
  setLoading: (loading: boolean) => void;
  clearAuth: () => void;
}

/**
 * Learning Store Adapter Interface
 * Abstracts learning progress state management
 */
export interface LearningStoreAdapter {
  // Progress Getters
  getProgress: () => any;
  getSyncStatus: () => 'idle' | 'syncing' | 'synced' | 'error';
  getLastSync: () => Date | null;

  // Progress Setters
  setProgress: (progress: any) => void;
  updateProgress: (updates: Partial<any>) => void;
  setSyncStatus: (status: 'idle' | 'syncing' | 'synced' | 'error') => void;
  markSynced: () => void;

  // Session Performance
  trackSessionPerformance: (metrics: {
    wordsLearned?: number;
    conversationTime?: number;
    accuracy?: number;
  }) => void;
}

/**
 * Chat Store Adapter Interface
 * Abstracts chat conversation state management
 */
export interface ChatStoreAdapter {
  // Message Management
  addMessage: (message: any) => void;
  updateMessage: (id: string, updates: Partial<any>) => void;
  clearMessages: () => void;

  // Conversation State
  setCurrentConversation: (conversationId: string | null) => void;
  getCurrentConversation: () => string | null;
  setLoading: (loading: boolean) => void;
}

/**
 * SRS Store Adapter Interface
 * Abstracts SRS card state management
 */
export interface SRSStoreAdapter {
  // Card Management
  getCards: () => any[];
  addCard: (card: any) => void;
  updateCard: (id: string, updates: Partial<any>) => void;
  removeCard: (id: string) => void;

  // Review State
  getDueCards: () => any[];
  getReviewSession: () => any | null;
  setReviewSession: (session: any | null) => void;
}

/**
 * Factory functions to create adapters from Zustand stores
 * These are used in production code to inject real stores
 */

export const createAuthStoreAdapter = (store: any): AuthStoreAdapter => ({
  getUser: () => store.getState().user,
  getSession: () => store.getState().session,
  getLoading: () => store.getState().loading,
  setUser: (user) => store.getState().setUser(user),
  setSession: (session) => store.getState().setSession(session),
  setLoading: (loading) => store.getState().setLoading(loading),
  clearAuth: () => store.getState().clearAuth(),
});

export const createLearningStoreAdapter = (store: any): LearningStoreAdapter => ({
  getProgress: () => store.getState().progress,
  getSyncStatus: () => store.getState().syncStatus,
  getLastSync: () => store.getState().lastSync,
  setProgress: (progress) => store.getState().setProgress(progress),
  updateProgress: (updates) => store.getState().updateProgress(updates),
  setSyncStatus: (status) => store.getState().setSyncStatus(status),
  markSynced: () => store.getState().markSynced(),
  trackSessionPerformance: (metrics) => store.getState().trackSessionPerformance(metrics),
});

export const createChatStoreAdapter = (store: any): ChatStoreAdapter => ({
  addMessage: (message) => store.getState().addMessage(message),
  updateMessage: (id, updates) => store.getState().updateMessage(id, updates),
  clearMessages: () => store.getState().clearMessages(),
  setCurrentConversation: (id) => store.getState().setCurrentConversation(id),
  getCurrentConversation: () => store.getState().currentConversation,
  setLoading: (loading) => store.getState().setLoading(loading),
});

export const createSRSStoreAdapter = (store: any): SRSStoreAdapter => ({
  getCards: () => store.getState().cards,
  addCard: (card) => store.getState().addCard(card),
  updateCard: (id, updates) => store.getState().updateCard(id, updates),
  removeCard: (id) => store.getState().removeCard(id),
  getDueCards: () => store.getState().getDueCards(),
  getReviewSession: () => store.getState().reviewSession,
  setReviewSession: (session) => store.getState().setReviewSession(session),
});
